import * as React from 'react'
import Tooltip from '@mui/material/Tooltip'

/**
 * Shared shell for the pills inside `PanelToggles`.
 *
 * Owns the Tooltip wrapper, the button frame (sizing, glassmorphism bg,
 * border, focus outline), and the active-vs-default state mapping. The
 * call site provides the icon, label, and icon-box rendering as
 * `children`, plus the accent token via `accentVar` / `accentRgbVar`.
 *
 * ### Accent via local CSS vars
 *
 * Tailwind's JIT compiler scans literal class strings — interpolating an
 * accent name into `tw-bg-[var(${name})]` defeats it. Instead we set
 * `--pill-accent` and `--pill-accent-rgb` via inline `style` from the
 * caller's chosen token, and the className references those locals
 * (which are real, literal strings to Tailwind).
 *
 * ### `tw-border-solid` is required
 *
 * Tailwind preflight is disabled in this repo, so `tw-border` only sets
 * width — the border is invisible without explicit border-style.
 */
export default function PillButton({
    isOpen,
    isMobile,
    onClick,
    ariaLabel,
    tooltipTitle,
    accentVar,
    accentRgbVar,
    dataTestid,
    children,
}) {
    // Desktop py is sized so the pill matches the right-side analytics
    // control bar from `@infranodus/infranodus-graph` (container `p-1.5`
    // + buttons `py-1.5 text-[13px]` ≈ 44px). The 32px icon chip
    // (16px icon + `tw-p-2`) drives this pill's height, so `tw-py-1.5`
    // here lands the outer button at the same ~44px.
    const sizing = isMobile
        ? 'tw-p-1.5 tw-rounded-xl'
        : 'tw-px-4 tw-py-1.5 tw-rounded-2xl'

    const base =
        'tw-flex tw-items-center tw-space-x-2.5 tw-backdrop-blur-md tw-shadow-md tw-cursor-pointer tw-border tw-border-solid focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-offset-2 focus-visible:tw-outline-[var(--pill-accent)] tw-transition-colors'

    const stateClasses = isOpen
        ? 'tw-bg-[rgb(var(--pill-accent-rgb)/0.1)] dark:tw-bg-[rgb(var(--pill-accent-rgb)/0.15)] tw-border-[var(--pill-accent)]'
        : 'tw-bg-white/90 dark:tw-bg-[#1e1e2e]/90 tw-border-gray-200 dark:tw-border-gray-700 hover:tw-bg-gray-100/80 dark:hover:tw-bg-gray-700/60 hover:tw-border-[var(--pill-accent)]'

    const style = {
        '--pill-accent': `var(${accentVar})`,
        '--pill-accent-rgb': `var(${accentRgbVar})`,
    }

    return (
        <Tooltip title={tooltipTitle} arrow>
            <button
                type="button"
                onClick={onClick}
                aria-label={ariaLabel}
                aria-pressed={isOpen}
                data-testid={dataTestid}
                style={style}
                className={`${base} ${sizing} ${stateClasses}`}
            >
                {children}
            </button>
        </Tooltip>
    )
}
